/**
 * DwAgentChatBar
 *
 * The same collapsed-pill / slide-up-panel chat bar as AmsCopilotChatBar
 * (identical .rql-pcb/.rql-chat__* visuals), but wired directly to one
 * Digital Workforce agent via the claude-managed /chat endpoint
 * (streamChat + agent_id) instead of the shared AMS Copilot amp — plus mic
 * dictation and file upload, the same two capabilities the full chat page's
 * ChatInput has, so this isn't a stripped-down "lite" composer.
 *
 * Mounted once, app-wide, by GlobalChatBar — not tied to any single page.
 * The New Chat page mounts it a second way, variant="page": the same bar
 * opened full-size in the main panel, with your recent chats with this
 * agent as its empty state.
 * Deliberately minimal on the conversation side — no tool-use timeline, no
 * artifact chips, no resumable tool-approval flow (those live in the full
 * chat page). If the agent's turn needs a step this bar can't render, it
 * says so and offers "Open full chat" instead of failing silently.
 */
import React, {
  useCallback, useEffect, useRef, useState, KeyboardEvent,
} from 'react';
import ReactMarkdown from 'react-markdown';
import CarrierHandoffFrame, { HANDOFF_LINK } from '../../features/chat/CarrierHandoffFrame';
import remarkGfm from 'remark-gfm';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiSend, FiX, FiChevronDown, FiExternalLink, FiPaperclip, FiMic, FiMapPin, FiUploadCloud, FiFolder, FiSquare } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { streamChat, interruptChat, sseErrorMessage, type SseEvent } from '../../features/claudeagents/claudeAgentApi';
import { uploadFile, uploadAmsFiles } from '../../services/fileUploadService';
import SelectFromAmsDialog, { type AmsUploadSelection } from '../../features/momentumcommand/SelectFromAmsDialog';
import type { ChatMessage as ChatMessageType } from '../../features/chat/chat.types';
import { useAgentReplySignal, type ActiveAgentInfo } from '../../context/ActiveAgentContext';
import AgentChatHistory from './AgentChatHistory';
import { withAreaContext } from '../../utils/areaContext';
import AttachmentTray, { isImageName, type TrayItem } from './AttachmentTray';
import { useInsuredMention, buildEntityXmlTag } from '../../features/chat/useInsuredMention';
import InsuredMentionDropdown, { type InsuredResult } from '../../features/chat/InsuredMentionDropdown';
import { processEntityLinks } from '../../features/chat/entityLinks';
import { useInsuredDetail } from '../../context/InsuredDetailContext';
import { usePolicyDetail } from '../../context/PolicyDetailContext';
import '../../styles/RateQuoteLink.css';
import '../../styles/InsuredDetail.css';

export interface DwAgentChatBarProps {
  agentId: string;
  agentName: string;
  avatarColor: string;
  avatarInitials: string;
  avatarPhoto?: string;
  /** Bump this (e.g. Date.now()) to force the panel open from outside — used
   *  by "Start Chat" buttons elsewhere so they expand this bar instead of
   *  navigating to a separate page. */
  openSignal?: number;
  /** The workspace/Center the current route belongs to (e.g. "Command
   *  Center"), if any. Shown to the user as a badge AND sent to the agent
   *  as real context on every message — not decorative on either end. */
  areaName?: string;
  /** The full active roster, for the avatar-click agent switcher. Clicking
   *  the picture when you're not already parked on one agent's own page is
   *  how you retarget this same bar (same area context) to someone else. */
  agentOptions?: ActiveAgentInfo[];
  onSwitchAgent?: (agent: ActiveAgentInfo) => void;
  /** "dock" (default): the collapsible pill floating over a page.
   *  "page": always open, filling its container — the New Chat page. */
  variant?: 'dock' | 'page';
  /** When set, sending hands the message (and any attached files) to the
   *  caller instead of this bar's own lightweight stream — the New Chat page
   *  uses it to start the full chat, so nothing needs "Open full chat". */
  /** `mentions` maps each @-linked display name in `text` to its record;
   *  `apiText` is `text` with those links as structured tags for an agent. */
  onSubmit?: (text: string, files: { name: string; url: string }[], mentions: Map<string, InsuredResult>, apiText: string) => void;
  /** A page target can offer "Select from AMS" in the attach menu. */
  onOpenAmsPicker?: () => void;
  /** When set (a page claimed this bar as its composer), files dropped or
   *  picked here go to the caller's own upload instead of this bar's. */
  onUploadFiles?: (files: File[]) => void;
  /** Disables sending while the caller is busy (saving / agent replying). */
  externalBusy?: boolean;
  /** Stops the caller's in-flight agent turn (POST /chat/interrupt). While
   *  `externalBusy` the Send button becomes a Stop button that calls this;
   *  without it the Send button just stays disabled as before. */
  onInterrupt?: () => void;
  /** The caller holds attachments of its own, so Send is allowed empty. */
  externalHasAttachments?: boolean;
  /** A page target's own attachments (a command's files), shown in the tray. */
  externalFiles?: TrayItem[];
  onRemoveExternalFile?: (key: string) => void;
}

interface PendingFile {
  name: string;
  url: string;
  /** For the attachment tray: stable key, size, and an image preview. */
  key?: string;
  size?: number;
  previewUrl?: string;
}

function nowStamp(): string {
  return new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function eventText(evt: { content?: Array<{ type: string; text?: string }> }): string {
  return (evt.content ?? []).filter((c) => c.type === 'text').map((c) => c.text ?? '').join('');
}

export function MdBubble({ text }: { text: string }) {
  // $$IDF_Name##id$$ / $$PDF_Name##id$$ in a reply become insured/policy links that
  // open the same detail panel as on the chat page.
  const { setSelectedInsuredId } = useInsuredDetail();
  const { setSelectedPolicyId } = usePolicyDetail();
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        a: ({ href, children, ...props }: any) => {
          // A carrier hand-off link (a code to type, a choice to make): show the waiting question right here as a card.
          if (typeof href === 'string' && HANDOFF_LINK.test(href)) {
            return <CarrierHandoffFrame url={href} />;
          }
          if (href?.startsWith('#INSURED_ID_')) {
            const insuredId = href.replace('#INSURED_ID_', '');
            return (
              <button type="button" className="insured-link" onClick={() => { setSelectedPolicyId(null); setSelectedInsuredId(insuredId); }}>
                {children}
              </button>
            );
          }
          if (href?.startsWith('#POLICY_ID_')) {
            const policyId = href.replace('#POLICY_ID_', '');
            return (
              <button type="button" className="insured-link insured-link--policy" onClick={() => { setSelectedInsuredId(null); setSelectedPolicyId(policyId); }}>
                {children}
              </button>
            );
          }
          return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
        },
        p:  ({ children }) => <p className="rql-pcb__md-p">{children}</p>,
        ul: ({ children }) => <ul className="rql-pcb__md-ul">{children}</ul>,
        ol: ({ children }) => <ol className="rql-pcb__md-ol">{children}</ol>,
        li: ({ children }) => <li className="rql-pcb__md-li">{children}</li>,
        strong: ({ children }) => <strong className="rql-pcb__md-strong">{children}</strong>,
        em:     ({ children }) => <em className="rql-pcb__md-em">{children}</em>,
        table: ({ children }) => <div className="rql-pcb__md-table-wrap"><table className="rql-pcb__md-table">{children}</table></div>,
        thead: ({ children }) => <thead className="rql-pcb__md-thead">{children}</thead>,
        tbody: ({ children }) => <tbody className="rql-pcb__md-tbody">{children}</tbody>,
        tr:    ({ children }) => <tr className="rql-pcb__md-tr">{children}</tr>,
        th:    ({ children }) => <th className="rql-pcb__md-th">{children}</th>,
        td:    ({ children }) => <td className="rql-pcb__md-td">{children}</td>,
        hr:    () => <hr className="rql-pcb__md-hr" />,
        blockquote: ({ children }) => <blockquote className="rql-pcb__md-blockquote">{children}</blockquote>,
      }}
    >
      {processEntityLinks(text)}
    </ReactMarkdown>
  );
}

const DwAgentChatBar: React.FC<DwAgentChatBarProps> = ({
  agentId, agentName, avatarColor, avatarInitials, avatarPhoto, openSignal, areaName,
  agentOptions, onSwitchAgent, variant = 'dock', onSubmit, onUploadFiles, externalBusy = false, onInterrupt, externalHasAttachments = false, onOpenAmsPicker, externalFiles, onRemoveExternalFile,
}) => {
  const navigate = useNavigate();
  const isPage = variant === 'page';
  // Docked bar handing its messages to a page (a command's conversation):
  // it stays the compact bar and never opens its own quick-chat panel.
  const panelless = !!onSubmit && !isPage;

  const [open,     setOpen]     = useState(isPage);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input,    setInput]    = useState('');
  const [thinking, setThinking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<{ key: string; name: string; size: number; previewUrl?: string }[]>([]);
  const [failedFiles, setFailedFiles] = useState<{ key: string; name: string; size: number; error: string }[]>([]);
  // Image previews for files handed to a page target, keyed by file name
  // (the target only knows names), so its tray cards still get thumbnails.
  const [targetPreviews, setTargetPreviews] = useState<Record<string, string>>({});
  const [uploadError, setUploadError] = useState('');
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef        = useRef<HTMLTextAreaElement>(null);
  const fileInputRef     = useRef<HTMLInputElement>(null);
  const sessionIdRef     = useRef<string | null>(null);
  const abortRef         = useRef<AbortController | null>(null);
  // Set when the user presses Stop on this bar's own quick-chat turn; read by
  // the stream handlers so the turn is finished as "stopped", not failed.
  const ownInterruptRef  = useRef(false);
  const recognitionRef   = useRef<any>(null);
  const micBaseValueRef  = useRef('');
  const switcherRef      = useRef<HTMLDivElement>(null);
  // This conversation's client-generated id: uploads are scoped to it (they can
  // happen before the first message), and from the first send it becomes the
  // conversationId sent on every turn (sessionIdRef). Reset on agent switch/clear.
  const uploadConvIdRef  = useRef(crypto.randomUUID());

  // Tell the Digital Team strip this agent is replying to you.
  useAgentReplySignal(agentId, thinking);

  // ── @mentions — link an insured, prospect, policy or quote ──
  // Same hook and dropdown as the Rate Quoting bar and the command panel.
  // Only the display name goes in the box (highlighted); the link travels
  // alongside it: structured tags for an agent, or the record map for a
  // page target (a command saves it in its own clickable-link format).
  const {
    mentionActive, mentionQuery, mentionResults, mentionLoading, mentionActiveIdx,
    setMentionActiveIdx, onValueChange: mentionOnValueChange, onKeyDown: mentionOnKeyDown,
    selectMention, closeMention, insertedEntities, insertedNames, clearInsertedNames,
  } = useInsuredMention();
  const liveMentionNames = insertedNames.filter((n) => input.includes(n));
  const hasMentions = liveMentionNames.length > 0;
  const renderWithMentions = (text: string): React.ReactNode => {
    const escaped = liveMentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
    return parts.map((part, i) => (liveMentionNames.includes(part)
      ? <span key={i} className="rql-pcb__mention-chip">{part}</span>
      : part));
  };
  const resizeInput = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };
  const handleMentionSelect = useCallback((ins: InsuredResult) => {
    const next = selectMention(ins, input).trimEnd() + ' ';
    setInput(next);
    setTimeout(() => {
      resizeInput();
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(next.length, next.length);
    }, 0);
  }, [input, selectMention]);


  // Attach menu ("Upload file" / "Select from AMS") — unless a page target
  // takes uploads without offering AMS; then the paperclip opens the file picker.
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  useEffect(() => {
    if (!attachMenuOpen) return;
    const close = () => setAttachMenuOpen(false);
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [attachMenuOpen]);

  // Switching which agent this bar targets starts a clean conversation —
  // it always talks to whichever agent is currently selected, never a
  // mix of two agents' turns.
  useEffect(() => {
    abortRef.current?.abort();
    recognitionRef.current?.stop();
    sessionIdRef.current = null;
    uploadConvIdRef.current = crypto.randomUUID();
    setMessages([]);
    setInput('');
    setThinking(false);
    setIsListening(false);
    setPendingFiles([]);
    setUploadError('');
  }, [agentId]);

  useEffect(() => {
    if (openSignal && !panelless) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  // Navigating to another page closes the docked bar's panel and menus; the
  // conversation and any draft are kept for when it's reopened.
  const { pathname } = useLocation();
  const lastPathRef = useRef(pathname);
  useEffect(() => {
    if (pathname === lastPathRef.current) return;
    lastPathRef.current = pathname;
    if (isPage) return;
    setOpen(false);
    setSwitcherOpen(false);
    setAttachMenuOpen(false);
  }, [pathname, isPage]);

  useEffect(() => () => {
    abortRef.current?.abort();
    recognitionRef.current?.stop();
  }, []);

  useEffect(() => {
    if (!switcherOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    };
    const handleEscape = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') setSwitcherOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [switcherOpen]);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    mentionOnValueChange(val);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  // Continues this bar's conversation in the full chat page when there is
  // one (same session hand-off the agent chat-history lists use), so the
  // steps this bar can't render pick up where they left off; otherwise
  // starts a fresh full chat with this agent.
  const openFullChat = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const sessionId = sessionIdRef.current;
    if (sessionId) {
      navigate(`/chat/${sessionId}`, { state: { dwAgentId: agentId, dwAgentName: agentName, dwSessionId: sessionId } });
      return;
    }
    const newConversationId = crypto.randomUUID();
    navigate(`/chat/${newConversationId}`, { state: { dwAgentId: agentId, dwAgentName: agentName } });
  }, [navigate, agentId, agentName]);

  // Dictation — same browser Web Speech API the full ChatInput uses, kept to
  // just what this bar needs: transcribe into the input, don't auto-send.
  const handleMicClick = useCallback(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in your browser. Try Chrome or Edge.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }
    if (!open && !panelless) setOpen(true);
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    micBaseValueRef.current = input ? input + ' ' : '';
    let finalTranscript = '';

    recognition.onresult = (event: any) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += (finalTranscript ? ' ' : '') + t.trim();
        else interim += t;
      }
      setInput(micBaseValueRef.current + finalTranscript + (interim ? ' ' + interim : ''));
    };
    recognition.onerror = (event: any) => {
      setIsListening(false);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.error('Speech recognition error:', event.error);
      }
    };
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, open, input, panelless]);

  // File upload — same fileUploadService the full chat uses; attaches by
  // URL/name into the next streamChat call, same as the main chat input.
  // Uploads one or more files (picker or drag-and-drop). Each shows as a
  // chip right away — spinner while uploading, then a check once it's
  // uploaded and ready to send with the next message.
  const uploadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    const previewFor = (file: File) => (isImageName(file.name) || file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined);
    if (onUploadFiles) {
      const previews: Record<string, string> = {};
      files.forEach((f) => { const u = previewFor(f); if (u) previews[f.name] = u; });
      if (Object.keys(previews).length) setTargetPreviews((prev) => ({ ...prev, ...previews }));
      onUploadFiles(files);
      return;
    }
    if (!open && !panelless) setOpen(true);
    setUploadError('');
    const batch = files.map((file) => ({
      key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: previewFor(file),
    }));
    setUploadingFiles((prev) => [...prev, ...batch.map(({ key, file, previewUrl }) => ({ key, name: file.name, size: file.size, previewUrl }))]);
    setUploading(true);
    await Promise.all(batch.map(async ({ key, file, previewUrl }) => {
      try {
        const res = await uploadFile(file, sessionIdRef.current ?? uploadConvIdRef.current);
        setPendingFiles((prev) => [...prev, { name: res.fileName || file.name, url: res.fileUrl, key, size: file.size, previewUrl }]);
      } catch (err: unknown) {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFailedFiles((prev) => [...prev, {
          key, name: file.name, size: file.size,
          error: err instanceof Error && err.message ? `Upload failed — ${err.message}` : 'Upload failed — try again',
        }]);
      } finally {
        setUploadingFiles((prev) => prev.filter((u) => u.key !== key));
      }
    }));
    setUploading(false);
  }, [open, panelless, onUploadFiles]);

  // "Select from AMS" when no page target supplies its own picker — the API
  // downloads the picked AMS files server-side (AMS download URLs are not
  // CORS-enabled) into the same upload path as uploadFile. A page target that
  // takes uploads but offers no picker keeps the plain file picker. Errors
  // propagate so the AMS dialog stays open and shows them.
  const [showAmsSelector, setShowAmsSelector] = useState(false);
  const handleAmsFilesUpload = useCallback(async (selections: AmsUploadSelection[]) => {
    if (!selections.length) return;
    if (!open && !panelless) setOpen(true);
    setUploadError('');
    setUploading(true);
    try {
      const refs = await uploadAmsFiles(selections, sessionIdRef.current ?? uploadConvIdRef.current);
      setPendingFiles((prev) => [...prev, ...refs.map((r) => ({
        name: r.name,
        url: r.url,
        key: `ams-${r.name}-${Math.random().toString(36).slice(2)}`,
        size: r.size,
      }))]);
    } finally {
      setUploading(false);
    }
  }, [open, panelless]);
  const openAmsPicker = onOpenAmsPicker ?? (onUploadFiles ? undefined : () => setShowAmsSelector(true));

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    void uploadFiles(files);
  }, [uploadFiles]);

  // Drag-and-drop, on any page: while files are being dragged anywhere in the
  // window the bar highlights as a drop target; dropping on the bar uploads
  // them. Only the bar accepts the drop, so pages with their own drop zones
  // (Command Center tasks, the scan tools) keep working.
  const [fileDragActive, setFileDragActive] = useState(false);
  const [overBar, setOverBar] = useState(false);
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const onEnter = (e: DragEvent) => { if (!hasFiles(e)) return; depth += 1; setFileDragActive(true); };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) { setFileDragActive(false); setOverBar(false); }
    };
    const reset = () => { depth = 0; setFileDragActive(false); setOverBar(false); };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', reset);
    window.addEventListener('dragend', reset);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', reset);
      window.removeEventListener('dragend', reset);
    };
  }, []);
  const dropHandlers = {
    onDragOver: (e: React.DragEvent) => {
      if (!Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (!overBar) setOverBar(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverBar(false);
    },
    onDrop: (e: React.DragEvent) => {
      if (!Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
      setFileDragActive(false);
      setOverBar(false);
      void uploadFiles(Array.from(e.dataTransfer.files));
    },
  };

  // Remove a card from the tray (a ready file, a failed one, or a target's).
  const removeTrayItem = useCallback((key: string) => {
    setPendingFiles((prev) => {
      const gone = prev.find((f) => (f.key ?? f.url) === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((f) => (f.key ?? f.url) !== key);
    });
    setFailedFiles((prev) => prev.filter((f) => f.key !== key));
    if (onRemoveExternalFile && externalFiles?.some((f) => f.key === key)) onRemoveExternalFile(key);
  }, [onRemoveExternalFile, externalFiles]);

  const trayItems: TrayItem[] = externalFiles
    ? externalFiles.map((f) => ({ ...f, previewUrl: f.previewUrl ?? targetPreviews[f.name] }))
    : [
      ...pendingFiles.map((f) => ({ key: f.key ?? f.url, name: f.name, size: f.size, status: 'ready' as const, previewUrl: f.previewUrl })),
      ...uploadingFiles.map((u) => ({ key: u.key, name: u.name, size: u.size, status: 'uploading' as const, previewUrl: u.previewUrl })),
      ...failedFiles.map((f) => ({ key: f.key, name: f.name, size: f.size, status: 'error' as const, error: f.error })),
    ];

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    // A page target may hold attachments of its own, so an empty send is
    // its call (it posts "Attached files" or ignores it).
    if ((!trimmed && pendingFiles.length === 0 && !onUploadFiles) || thinking || externalBusy) return;

    // The linked records actually still in the text, and the agent-facing
    // version of the text with each link as a structured tag.
    const mentions = new Map<string, InsuredResult>();
    let tagged = trimmed;
    insertedEntities.forEach((entity, name) => {
      if (!trimmed.includes(name)) return;
      mentions.set(name, entity);
      tagged = tagged.replace(name, `${name} ${buildEntityXmlTag(entity)}`);
    });
    clearInsertedNames();
    closeMention();

    if (onSubmit) {
      onSubmit(trimmed, pendingFiles, mentions, tagged);
      setInput('');
      setPendingFiles([]);
      setFailedFiles([]);
      return;
    }

    if (!open && !panelless) setOpen(true);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    const attachments = pendingFiles;
    setPendingFiles([]);

    const displayContent = [trimmed, ...attachments.map((a) => `📎 ${a.name}`)].filter(Boolean).join('\n');
    const userMsg: ChatMessageType = {
      id: `u-${Date.now()}`, role: 'user', content: displayContent, timestamp: nowStamp(),
    };
    setMessages(prev => [...prev, userMsg]);
    setThinking(true);

    const aiMsgId = `ai-${Date.now()}`;
    let closedText = '';
    let liveText = '';
    let bubbleOpen = false;

    const ensureBubble = () => {
      if (bubbleOpen) return;
      bubbleOpen = true;
      setMessages(prev => [...prev, { id: aiMsgId, role: 'assistant', content: '', timestamp: nowStamp() }]);
    };
    const render = () => {
      setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, content: closedText + liveText } : m)));
    };

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // The agent sees where you're asking from — a real signal sent with the
    // turn, not just a badge shown to the user. Only sent on the first
    // message of a session; once a session exists the agent already has it
    // in context, and re-stating it on every turn would be noise.
    const rawText = tagged || 'See attached file(s).';
    const isFirstTurn = !sessionIdRef.current;
    const apiText = (areaName && isFirstTurn)
      ? withAreaContext(rawText, areaName)
      : rawText;

    // Same contract as the full chat (ChatContainer): the client owns the
    // conversation id and sends it on every turn — the /chat endpoint does not
    // hand one back. It's the id uploads were already scoped to, and it stays
    // fixed for this conversation so "Open full chat" continues the same thread.
    const conversationId = sessionIdRef.current ?? uploadConvIdRef.current;
    sessionIdRef.current = conversationId;

    try {
      await streamChat(
        apiText,
        conversationId,
        (evt: SseEvent) => {
          if (evt.type === 'answer') {
            // /chat replies as answer events: partials carry text in `response`;
            // the terminal one (responseStreaming === false) repeats the last
            // chunk, so it's only used when nothing streamed (error-only turns).
            const isTerminal = evt.responseStreaming === false;
            if (evt.response && (!isTerminal || !(closedText + liveText))) {
              ensureBubble();
              liveText += evt.response;
              render();
            }
          } else if (evt.type === 'assistant.message' || evt.type === 'message.delta') {
            const chunk = eventText(evt);
            if (chunk) {
              ensureBubble();
              liveText += chunk;
              render();
            }
          } else if (evt.type === 'agent.message') {
            const full = eventText(evt);
            if (full) {
              ensureBubble();
              closedText += full;
              liveText = '';
              render();
            }
          } else if (evt.type === 'error') {
            // Keep any answer that already streamed and add the reason below it
            // (e.g. a turn cut off by BUDGET_REACHED or a terminated session),
            // as the full chat does. A turn the user stopped is not an error:
            // the backend's "empty response" for a stop before any text is
            // shown as a plain stopped note — unless the session was actually
            // terminated, which outranks the stop.
            ensureBubble();
            const streamed = closedText + liveText;
            const stopped = ownInterruptRef.current && !evt.terminated;
            const reason = stopped ? 'Response stopped.' : sseErrorMessage(evt);
            const marker = stopped ? '⏹' : '⚠️';
            closedText = streamed ? `${streamed}\n\n${marker} ${reason}` : reason;
            liveText = '';
            render();
          } else if (evt.type === 'session.status_idle') {
            setThinking(false);
          }
        },
        controller.signal,
        undefined,
        attachments.length ? attachments.map((a) => a.url) : undefined,
        attachments.length ? attachments.map((a) => a.name) : undefined,
        undefined,
        agentId,
      );
      if (ownInterruptRef.current) {
        // Stopped after some text had streamed (no error event in that case):
        // close the answer with a stopped note.
        ownInterruptRef.current = false;
        if (bubbleOpen && !(closedText + liveText).includes('Response stopped.')) {
          closedText = `${closedText}${liveText}\n\n⏹ Response stopped.`;
          liveText = '';
          render();
        }
      }
      if (!bubbleOpen) {
        setMessages(prev => [...prev, {
          id: aiMsgId, role: 'assistant',
          content: `That needs a step this quick chat can't show — use "Open full chat" above to continue with ${agentName}.`,
          timestamp: nowStamp(),
        }]);
      }
    } catch (err: unknown) {
      if ((err as Error)?.name === 'AbortError') return;
      if (bubbleOpen) {
        setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, content: 'Connection issue. Please try again.' } : m)));
      } else {
        setMessages(prev => [...prev, {
          id: aiMsgId, role: 'assistant', content: 'Connection issue. Please try again.', timestamp: nowStamp(),
        }]);
      }
    } finally {
      ownInterruptRef.current = false;
      setThinking(false);
    }
  }, [thinking, open, agentId, agentName, pendingFiles, areaName, onSubmit, onUploadFiles, externalBusy, panelless, insertedEntities, clearInsertedNames, closeMention]);

  // Stop this bar's own quick-chat turn (no page target). The backend sends
  // user.interrupt; the stream then ends normally with the partial answer.
  const stopOwnTurn = useCallback(() => {
    const cid = sessionIdRef.current;
    if (!cid || !thinking) return;
    ownInterruptRef.current = true;
    interruptChat(cid).catch((err) => {
      // Usually a 409: the turn finished before the click landed.
      ownInterruptRef.current = false;
      console.warn('interruptChat failed', err);
    });
  }, [thinking]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionActive) {
      const consumed = mentionOnKeyDown(e, input, (val) => { setInput(val); setTimeout(resizeInput, 0); });
      if (consumed) return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMessages([]);
    sessionIdRef.current = null;
    uploadConvIdRef.current = crypto.randomUUID();
  };

  const switchableAgents = (agentOptions ?? []).filter((a) => a.id !== agentId);
  const canSwitchAgent = switchableAgents.length > 0 && !!onSwitchAgent;

  // The picture is the switcher when there's someone to switch to — picking
  // a name re-targets this same bar (same area context) to them instead.
  // With nothing to switch to, it just opens/collapses the panel as before.
  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canSwitchAgent) {
      setSwitcherOpen((o) => !o);
    } else if (!isPage && !panelless) {
      setOpen((o) => !o);
    }
  };

  const handleSelectAgent = (a: ActiveAgentInfo) => {
    setSwitcherOpen(false);
    onSwitchAgent?.(a);
    if (!panelless) setOpen(true);
  };

  const hasMessages = messages.length > 0;
  const canSend = !!input.trim() || pendingFiles.length > 0 || externalHasAttachments;
  const suggestions = [
    'What have you been working on?',
    "What's on your plate right now?",
    'Summarize your last few tasks',
  ];

  // Agents render as rounded squares on a neutral tile (see utils/agentAvatar).
  const badgeAvatar = avatarPhoto
    ? <img src={avatarPhoto} alt="" style={{ width: '100%', height: '100%', borderRadius: 'var(--radius-xs)', objectFit: 'cover', boxSizing: 'border-box', background: avatarColor, border: '1px solid var(--border-glass)' }} />
    : <span style={{
        width: '100%', height: '100%', borderRadius: 'var(--radius-xs)', background: avatarColor,
        boxSizing: 'border-box', border: '1px solid var(--border-glass)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-primary)', fontSize: 11, fontWeight: 700,
      }}>{avatarInitials}</span>;
  const agentTileStyle: React.CSSProperties = {
    background: avatarColor, borderRadius: 'var(--radius-xs)', boxSizing: 'border-box',
    border: '1px solid var(--border-glass)', color: 'var(--text-primary)', overflow: 'hidden',
  };

  return (
    <div
      className={`rql-pcb${open ? ' rql-pcb--open' : ''}${isPage ? ' rql-pcb--page' : ''}${fileDragActive ? ' rql-pcb--drag' : ''}${overBar ? ' rql-pcb--drag-over' : ''}`}
      {...dropHandlers}
    >
      {fileDragActive && (
        <div className="rql-pcb__drop-hint" aria-hidden="true">
          <FiUploadCloud size={15} />
          {overBar ? 'Release to attach' : `Drop files here to send to ${agentName}`}
        </div>
      )}

      {/* ── Messages panel (slides up above the bar) ── */}
      <div className={`rql-pcb__panel${open ? ' rql-pcb__panel--visible' : ''}`} aria-hidden={!open}>
        {/* Full-size (page) and still empty: the intro below already says who
            this is, and none of the header's actions apply yet. */}
        {(!isPage || hasMessages) && (
        <div className="rql-pcb__panel-hdr">
          <div className="rql-pcb__panel-title">
            <span className="rql-pcb__panel-orb" style={agentTileStyle}>
              {avatarPhoto
                ? <img src={avatarPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : avatarInitials}
            </span>
            <span>{agentName}</span>
            {areaName && (
              <span className="rql-pcb__area-pill" title={`This chat knows you're viewing ${areaName}`}>
                <FiMapPin size={9} />
                {areaName}
              </span>
            )}
          </div>
          <div className="rql-pcb__panel-hdr-right">
            <button type="button" className="rql-pcb__icon-btn" title="Open full chat" onClick={openFullChat}>
              <FiExternalLink size={12} />
            </button>
            {hasMessages && (
              <button type="button" className="rql-pcb__icon-btn" title="Clear conversation" onClick={clear}>
                <FiX size={12} />
              </button>
            )}
            {!isPage && (
              <button type="button" className="rql-pcb__icon-btn" title="Collapse" onClick={() => setOpen(false)}>
                <FiChevronDown size={13} />
              </button>
            )}
          </div>
        </div>
        )}

        <div className={`rql-pcb__msgs${!hasMessages && isPage ? ' rql-pcb__msgs--intro' : ''}`}>
          {!hasMessages && isPage ? (
            <div className="rql-pcb__intro">
              <div className="rql-pcb__intro-head">
                <span className="rql-pcb__intro-avatar" style={{ ...agentTileStyle, borderRadius: 'var(--radius-md)' }}>
                  {avatarPhoto ? <img src={avatarPhoto} alt="" /> : avatarInitials}
                </span>
                <div className="rql-pcb__intro-text">
                  <h1 className="rql-pcb__intro-name">{agentName}</h1>
                  <p className="rql-pcb__intro-sub">
                    {areaName
                      ? <>Messages go straight to {agentName}, who'll know you're asking about <strong>{areaName}</strong>.</>
                      : <>Messages go straight to {agentName}. Start something new, or pick up a recent chat.</>}
                  </p>
                </div>
              </div>
              <div className="rql-pcb__intro-chips">
                {suggestions.map(s => (
                  <button key={s} type="button" className="rql-pcb__chip" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
              <AgentChatHistory agentId={agentId} agentName={agentName} />
            </div>
          ) : !hasMessages ? (
            <div className="rql-pcb__empty">
              <div className="rql-pcb__empty-glyph"><RiSparklingFill size={22} /></div>
              <p className="rql-pcb__empty-title">{agentName}</p>
              <p className="rql-pcb__empty-sub">
                {areaName
                  ? <>Chat directly with this agent — they'll know you're asking about <strong>{areaName}</strong>.</>
                  : 'Chat directly with this agent — messages here reach them right away.'}
              </p>
              <div className="rql-pcb__chips">
                {suggestions.map(s => (
                  <button key={s} type="button" className="rql-pcb__chip" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className={`rql-pcb__row rql-pcb__row--${msg.role === 'assistant' ? 'ai' : 'user'}`}>
                {msg.role === 'assistant' && (
                  <span className="rql-pcb__avatar" style={{ background: avatarColor, overflow: 'hidden', borderRadius: 'var(--radius-xs)', boxSizing: 'border-box', border: '1px solid var(--border-glass)' }}>
                    {avatarPhoto
                      ? <img src={avatarPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: 8 }}>{avatarInitials}</span>}
                  </span>
                )}
                <div className="rql-pcb__bubble rql-pcb__bubble--content">
                  <div className="rql-pcb__bubble-text">
                    <MdBubble text={msg.content} />
                  </div>
                </div>
              </div>
            ))
          )}

          {thinking && (
            <div className="rql-pcb__row rql-pcb__row--ai">
              <span className="rql-pcb__avatar" style={{ background: avatarColor, overflow: 'hidden', borderRadius: 'var(--radius-xs)', boxSizing: 'border-box', border: '1px solid var(--border-glass)' }}>
                {avatarPhoto
                  ? <img src={avatarPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 8 }}>{avatarInitials}</span>}
              </span>
              <div className="rql-pcb__bubble rql-pcb__bubble--thinking">
                <span className="rql-pcb__dot" />
                <span className="rql-pcb__dot" />
                <span className="rql-pcb__dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* ── Always-visible input bar ── */}
      <div className={`rql-pcb__bar rql-pcb__bar--stack${trayItems.length ? ' rql-pcb__bar--has-files' : ''}`} role="region" aria-label={`Chat with ${agentName}`}>
        {/* Attached files live inside the bar, above where you type. */}
        <AttachmentTray items={trayItems} onRemove={removeTrayItem} />
        {uploadError && <div className="rql-pcb__attachment-error">{uploadError}</div>}
        <div className="rql-pcb__bar-row">
        <div className="rql-pcb__badge-wrap" ref={switcherRef}>
          <button
            type="button"
            className="rql-pcb__bar-badge"
            onClick={handleAvatarClick}
            title={
              canSwitchAgent
                ? `Chat with ${agentName}${areaName ? ` — about ${areaName}` : ''} (click to switch agent)`
                : open && !isPage ? 'Collapse'
                : areaName ? `Chat with ${agentName} — about ${areaName}` : `Chat with ${agentName}`
            }
            aria-haspopup={canSwitchAgent ? 'listbox' : undefined}
            aria-expanded={canSwitchAgent ? switcherOpen : open}
            style={{ background: 'transparent', padding: 2, borderRadius: 'var(--radius-sm)' }}
          >
            {badgeAvatar}
            {hasMessages && !open && (
              <span className="rql-pcb__badge-count">{messages.filter(m => m.role === 'assistant').length}</span>
            )}
            {canSwitchAgent && <span className="rql-pcb__badge-switch-hint" aria-hidden="true" />}
          </button>

          {switcherOpen && canSwitchAgent && (
            <div className="rql-pcb__agent-switcher" role="listbox" aria-label="Switch agent">
              <div className="rql-pcb__agent-switcher-hdr">
                {areaName ? `Ask someone else about ${areaName}` : 'Switch agent'}
              </div>
              {switchableAgents.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="rql-pcb__agent-switcher-item"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleSelectAgent(a)}
                >
                  <span className="rql-pcb__agent-switcher-avatar" style={{ ...agentTileStyle, background: a.avatarColor }}>
                    {a.avatarPhoto
                      ? <img src={a.avatarPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : a.avatarInitials}
                  </span>
                  <span className="rql-pcb__agent-switcher-name">{a.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.json"
        />
        <div className="rql-pcb__menu-anchor" onMouseDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="rql-pcb__icon-btn rql-pcb__attach-btn"
            onClick={() => {
              if (openAmsPicker) setAttachMenuOpen((o) => !o);
              else fileInputRef.current?.click();
            }}
            title="Attach a file"
            aria-label="Attach a file"
            aria-haspopup={openAmsPicker ? 'menu' : undefined}
            aria-expanded={openAmsPicker ? attachMenuOpen : undefined}
            disabled={uploading}
          >
            <FiPaperclip size={14} />
          </button>
          {attachMenuOpen && openAmsPicker && (
            <div className="rql-pcb__menu" role="menu" aria-label="Attach">
              <button type="button" role="menuitem" className="rql-pcb__menu-item" onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}>
                <FiPaperclip size={13} /> Upload file
              </button>
              <button type="button" role="menuitem" className="rql-pcb__menu-item" onClick={() => { setAttachMenuOpen(false); openAmsPicker(); }}>
                <FiFolder size={13} /> Select from AMS
              </button>
            </div>
          )}
        </div>


        <div className="rql-pcb__inputwrap-stretch rql-pcb__inputwrap-rel">
          {mentionActive && (
            <InsuredMentionDropdown
              query={mentionQuery}
              results={mentionResults}
              loading={mentionLoading}
              activeIdx={mentionActiveIdx}
              onSelect={handleMentionSelect}
              onActiveChange={setMentionActiveIdx}
            />
          )}
          <div className="rql-chat__inputwrap">
            <span className="rql-chat__input-icon"><RiSparklingFill size={12} /></span>
            <div className="rql-pcb__input-sizer">
              {hasMentions && (
                <div className="rql-pcb__mention-overlay" aria-hidden>
                  {renderWithMentions(input)}
                </div>
              )}
              <textarea
                ref={inputRef}
                className={`rql-chat__input${hasMentions ? ' rql-pcb__input--mentions' : ''}`}
                rows={1}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => { if (!open && !panelless) setOpen(true); }}
                placeholder={isListening ? 'Listening…' : areaName ? `Ask ${agentName} about ${areaName}… (@ to link a record)` : `Message ${agentName}… (@ to link a record)`}
                aria-label={`Message ${agentName}`}
                disabled={thinking || externalBusy}
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          className={`rql-pcb__icon-btn rql-pcb__mic-btn${isListening ? ' rql-pcb__mic-btn--active' : ''}`}
          onClick={handleMicClick}
          title={isListening ? 'Stop dictation' : 'Dictate'}
          aria-label={isListening ? 'Stop dictation' : 'Dictate'}
          aria-pressed={isListening}
        >
          <FiMic size={14} />
        </button>

        {(onInterrupt ? externalBusy : (!onSubmit && thinking)) ? (
          // The agent is replying: Send becomes Stop. A page target without an
          // onInterrupt keeps the disabled Send button (its turn is not ours
          // to stop).
          <button
            type="button"
            className="rql-chat__send rql-chat__send--stop"
            onClick={onInterrupt ?? stopOwnTurn}
            title="Stop generating"
            aria-label="Stop generating"
          >
            <FiSquare size={12} />
          </button>
        ) : (
          <button
            type="button"
            className={`rql-chat__send${canSend ? ' rql-chat__send--active' : ''}`}
            onClick={() => send(input)}
            disabled={!canSend || thinking || externalBusy}
            aria-label="Send message"
          >
            <FiSend size={13} />
          </button>
        )}
        </div>
      </div>
      {/* Select from AMS Drive — browse a client's AMS files and attach them to this chat */}
      <SelectFromAmsDialog
        visible={showAmsSelector}
        onClose={() => setShowAmsSelector(false)}
        onUpload={handleAmsFilesUpload}
      />
    </div>
  );
};

export default DwAgentChatBar;
